// ===========================================
// USE RECOMMENDATION HOOK
// ===========================================
// Returns the optimal action from SERVER recommendations.
// Server sends ACTION_RECOMMENDATION and INSURANCE_RECOMMENDATION events.
// Both autoplay and BUDDY! consume the same server-authoritative recommendations.
// This is the SINGLE SOURCE OF TRUTH for all recommendations.
//
// ARCHITECTURE: Client-Driven Autoplay
// - Server calculates recommendations using basic strategy + Hi-Lo deviations
// - Server emits ACTION_RECOMMENDATION / INSURANCE_RECOMMENDATION events
// - Client stores recommendations in gameStateStore
// - If autoplay ON: client auto-sends player_action back to server
// - If autoplay OFF: BUDDY! displays the recommendation
// ===========================================

import { useMemo, useEffect, useRef, useCallback } from 'react';
import { useGameStateStore } from '../stores/gameStateStore';
import { useCardTrackingStore } from '../stores/cardTrackingStore';
import { useAuthStore } from '../stores/authStore';
import { useCoachStore } from '../stores/coachStore';
import { GamePhase } from '@busted-blackjack/shared';
import { api } from '../api';

// ===========================================
// TYPES
// ===========================================

export type StrategyAction = 'HIT' | 'STAND' | 'DOUBLE' | 'SPLIT' | 'SURRENDER';

export interface UseRecommendationResult {
  /** Recommended action: HIT, STAND, DOUBLE, SPLIT, SURRENDER, or null if not applicable */
  recommendedAction: StrategyAction | null;
  /** Explanation for the recommendation */
  reason: string;
  /** True if this is a count-based deviation from basic strategy */
  isDeviation: boolean;
  /** Insurance recommendation when dealer shows Ace */
  insuranceRecommendation: 'ACCEPT' | 'DECLINE' | null;
  /** Current true count (for display purposes) */
  trueCount: number;
  /** Whether recommendations are enabled */
  enabled: boolean;
  /** Number of cards in current hand (used to detect new cards dealt) */
  handCardCount: number;
  /** True if this is a "safe" action with no risk (e.g., HIT on ≤11, SURRENDER) */
  isSafeAction: boolean;
}

// ===========================================
// HOOK
// ===========================================

export function useRecommendation(): UseRecommendationResult {
  // Get user preference
  const showRecommendations = useAuthStore((state) => state.user?.showRecommendations ?? false);

  // Get server recommendations from store (populated by useGameEvents)
  const serverRecommendation = useGameStateStore((state) => state.serverRecommendation);
  const serverInsuranceRecommendation = useGameStateStore((state) => state.serverInsuranceRecommendation);

  // Get game state for context
  const phase = useGameStateStore((state) => state.phase);
  const mySeatPosition = useGameStateStore((state) => state.mySeatPosition);
  const currentTurnSeat = useGameStateStore((state) => state.currentTurnSeat);
  const currentHandIndex = useGameStateStore((state) => state.currentHandIndex);
  const players = useGameStateStore((state) => state.players);
  const dealer = useGameStateStore((state) => state.dealer);
  const insuranceOffered = useGameStateStore((state) => state.insuranceOffered);
  const tableId = useGameStateStore((state) => state.tableId);

  // Get counting state (for display only - server does actual calculation)
  const trueCount = useCardTrackingStore((state) => state.trueCount);

  // Compute recommendation from server state
  const recommendation = useMemo((): UseRecommendationResult => {
    const nullResult: UseRecommendationResult = {
      recommendedAction: null,
      reason: '',
      isDeviation: false,
      insuranceRecommendation: null,
      trueCount,
      enabled: showRecommendations,
      handCardCount: 0,
      isSafeAction: false,
    };

    // Check if recommendations are enabled
    if (!showRecommendations) {
      return nullResult;
    }

    // Check if we have a seat
    if (mySeatPosition === null) {
      return nullResult;
    }

    // Get player data
    const player = players.get(mySeatPosition);

    // Handle insurance recommendation FIRST
    // Insurance is offered during DEALING phase, before PLAYING
    if (insuranceOffered && serverInsuranceRecommendation && player && !player.insuranceDecided) {
      const insuranceAction = serverInsuranceRecommendation.action === 'INSURANCE' ? 'ACCEPT' : 'DECLINE';
      return {
        recommendedAction: null,
        reason: serverInsuranceRecommendation.reason,
        isDeviation: serverInsuranceRecommendation.isDeviation,
        insuranceRecommendation: insuranceAction,
        trueCount,
        enabled: true,
        handCardCount: 0,
        isSafeAction: false,
      };
    }

    // For non-insurance recommendations, we need server recommendation and to be in PLAYING phase
    if (!serverRecommendation || phase !== GamePhase.PLAYING) {
      return nullResult;
    }

    // Verify it's for us and it's our turn
    if (serverRecommendation.seatPosition !== mySeatPosition || currentTurnSeat !== mySeatPosition) {
      return nullResult;
    }

    // Get current hand for card count
    if (!player || !player.hands || player.hands.length === 0) {
      return nullResult;
    }

    const handIndex = currentHandIndex ?? 0;
    const hand = player.hands[handIndex];
    if (!hand || !hand.cards) {
      return nullResult;
    }

    // Calculate hand score for safe action detection
    let score = 0;
    let aces = 0;
    for (const card of hand.cards) {
      if (card.rank === 'A') {
        aces++;
        score += 11;
      } else if (['K', 'Q', 'J'].includes(card.rank)) {
        score += 10;
      } else {
        score += parseInt(card.rank, 10);
      }
    }
    while (score > 21 && aces > 0) {
      score -= 10;
      aces--;
    }
    const isSoft = aces > 0 && score <= 21;

    // Determine if this is a "safe" action (no risk/neutral outcome)
    const action = serverRecommendation.action;
    const isSafeAction =
      action === 'SURRENDER' ||
      action === 'SPLIT' ||
      (action === 'HIT' && (score <= 11 || isSoft));

    return {
      recommendedAction: action,
      reason: serverRecommendation.reason,
      isDeviation: serverRecommendation.isDeviation,
      insuranceRecommendation: null,
      trueCount,
      enabled: true,
      handCardCount: hand.cards.length,
      isSafeAction,
    };
  }, [
    showRecommendations,
    serverRecommendation,
    serverInsuranceRecommendation,
    phase,
    mySeatPosition,
    currentTurnSeat,
    currentHandIndex,
    players,
    insuranceOffered,
    trueCount,
  ]);

  // ===========================================
  // COACH MESSAGE INTEGRATION
  // ===========================================

  const addCoachMessage = useCoachStore((state) => state.addMessage);
  const updateMessageOutcome = useCoachStore((state) => state.updateMessageOutcome);
  const markMessageIgnored = useCoachStore((state) => state.markMessageIgnored);
  const setCoachEnabled = useCoachStore((state) => state.setEnabled);

  // Helper to record outcome in NBA audit log (fire and forget)
  const recordOutcome = useCallback((outcome: string, isInsurance = false) => {
    if (!tableId || !showRecommendations) return;
    api.nbaAudit.recordOutcome({
      tableSlug: tableId,
      outcome,
      isInsurance,
    }).catch(() => {
      // Silently ignore - non-critical
    });
  }, [tableId, showRecommendations]);

  // Track natural blackjack hands that have been logged (to avoid double-logging)
  const loggedBlackjacksRef = useRef<Set<string>>(new Set());

  // Detect and log natural blackjack hands
  useEffect(() => {
    if (!showRecommendations || !tableId || mySeatPosition === null) return;

    const player = players.get(mySeatPosition);
    if (!player || !player.hands) return;

    player.hands.forEach((hand, handIndex) => {
      if (
        hand.cards?.length === 2 &&
        hand.outcome === 'blackjack' &&
        !loggedBlackjacksRef.current.has(`${tableId}-${handIndex}-${hand.cards[0]?.rank}${hand.cards[1]?.rank}`)
      ) {
        const blackjackKey = `${tableId}-${handIndex}-${hand.cards[0]?.rank}${hand.cards[1]?.rank}`;
        loggedBlackjacksRef.current.add(blackjackKey);

        api.nbaAudit.log({
          tableSlug: tableId,
          recommendedAction: 'BLACKJACK',
          isDeviation: false,
          actualAction: 'BLACKJACK',
          autoExecuted: true,
        }).then(() => {
          api.nbaAudit.recordOutcome({
            tableSlug: tableId,
            outcome: 'BLACKJACK',
            isInsurance: false,
          }).catch(() => {});
        }).catch(() => {});
      }
    });

    if (!player.hands.length || player.hands.every(h => !h.cards?.length)) {
      loggedBlackjacksRef.current.clear();
    }
  }, [showRecommendations, tableId, mySeatPosition, players]);

  // Track previous recommendation to detect changes
  const prevRecommendationRef = useRef<{
    action: StrategyAction | null;
    insurance: 'ACCEPT' | 'DECLINE' | null;
    handCardCount: number;
    handIndex: number | null;
    numHands: number;
  }>({ action: null, insurance: null, handCardCount: 0, handIndex: null, numHands: 0 });

  // Track message IDs for outcome updates
  interface HandMessageInfo {
    messageId: string;
    wasSafe: boolean;
    recommendedAction: StrategyAction;
  }
  const messageIdsRef = useRef<{
    handMessages: Map<number, HandMessageInfo>;
    lastActionMessageId: string | null;
    lastActionWasSafe: boolean;
    lastRecommendedAction: StrategyAction | null;
    lastInsuranceMessageId: string | null;
    lastInsuranceRecommendation: 'ACCEPT' | 'DECLINE' | null;
  }>({
    handMessages: new Map(),
    lastActionMessageId: null,
    lastActionWasSafe: false,
    lastRecommendedAction: null,
    lastInsuranceMessageId: null,
    lastInsuranceRecommendation: null
  });

  // Sync coach enabled state
  useEffect(() => {
    setCoachEnabled(showRecommendations);
  }, [showRecommendations, setCoachEnabled]);

  // Push coach messages when recommendations change
  useEffect(() => {
    if (!showRecommendations) return;

    const prev = prevRecommendationRef.current;
    const { recommendedAction, insuranceRecommendation, reason, isDeviation, trueCount: tc, handCardCount, isSafeAction } = recommendation;

    // Handle insurance recommendation change
    if (insuranceRecommendation && insuranceRecommendation !== prev.insurance) {
      const tcDisplay = tc >= 0 ? `+${tc.toFixed(1)}` : tc.toFixed(1);
      let msgId: string;
      if (insuranceRecommendation === 'ACCEPT') {
        msgId = addCoachMessage(
          'insurance',
          `TAKE INSURANCE\nTrue Count is ${tcDisplay}`,
          'At True Count ≥ +3, the deck is rich in 10s. Dealer blackjack rate exceeds the 33.3% break-even point.'
        );
      } else {
        msgId = addCoachMessage(
          'insurance',
          `DECLINE INSURANCE\nTrue Count is ${tcDisplay}`,
          'Insurance has a 7.4% house edge at neutral/negative counts.'
        );
      }
      messageIdsRef.current.lastInsuranceMessageId = msgId;
      messageIdsRef.current.lastInsuranceRecommendation = insuranceRecommendation;
      prev.insurance = insuranceRecommendation;
    }

    // Handle action recommendation change OR new card dealt OR switched to different hand
    const actionChanged = recommendedAction !== prev.action;
    const newCardDealt = handCardCount > prev.handCardCount && handCardCount > 0;
    const handIndexChanged = currentHandIndex !== prev.handIndex && currentHandIndex !== null;

    const currentPlayer = mySeatPosition !== null ? players.get(mySeatPosition) : null;
    const currentNumHands = currentPlayer?.hands?.length ?? 0;
    const splitJustHappened = currentNumHands > prev.numHands && prev.numHands > 0;

    if (recommendedAction && (actionChanged || newCardDealt || handIndexChanged)) {
      // Handle previous message outcome
      if (messageIdsRef.current.lastActionMessageId) {
        const lastRec = messageIdsRef.current.lastRecommendedAction;
        const playerHit = handCardCount > prev.handCardCount;
        const playerStood = !playerHit && !handIndexChanged;

        let ignored = false;
        if (lastRec === 'HIT' && playerStood) ignored = true;
        else if (lastRec === 'STAND' && playerHit) ignored = true;
        else if (lastRec === 'STAND' && splitJustHappened) ignored = true;
        else if (lastRec === 'DOUBLE' && playerStood) ignored = true;
        else if (lastRec === 'DOUBLE' && splitJustHappened) ignored = true;
        else if (lastRec === 'HIT' && splitJustHappened) ignored = true;

        if (ignored) {
          markMessageIgnored(messageIdsRef.current.lastActionMessageId);
        }

        if (!handIndexChanged) {
          if (messageIdsRef.current.lastActionWasSafe) {
            updateMessageOutcome(messageIdsRef.current.lastActionMessageId, 'neutral');
          } else {
            updateMessageOutcome(messageIdsRef.current.lastActionMessageId, 'good');
          }
        }
      }

      const messageType = isDeviation ? 'deviation' : 'recommendation';
      const msgId = addCoachMessage(
        messageType,
        reason,
        isDeviation ? `True Count: ${tc >= 0 ? '+' : ''}${tc.toFixed(1)}` : undefined,
        recommendedAction
      );

      const handIdx = currentHandIndex ?? 0;
      messageIdsRef.current.handMessages.set(handIdx, {
        messageId: msgId,
        wasSafe: isSafeAction,
        recommendedAction: recommendedAction,
      });

      messageIdsRef.current.lastActionMessageId = msgId;
      messageIdsRef.current.lastActionWasSafe = isSafeAction;
      messageIdsRef.current.lastRecommendedAction = recommendedAction;
      prev.action = recommendedAction;
      prev.handCardCount = handCardCount;
      prev.handIndex = currentHandIndex;
      prev.numHands = currentNumHands;
    }

    // When recommendation becomes null
    if (!recommendedAction && !insuranceRecommendation) {
      const lastRec = messageIdsRef.current.lastRecommendedAction;
      const lastMsgId = messageIdsRef.current.lastActionMessageId;

      if (lastMsgId && lastRec && prev.handCardCount > 0 && phase === GamePhase.PLAYING) {
        const player = mySeatPosition !== null ? players.get(mySeatPosition) : null;
        const currentHand = player?.hands?.[prev.handIndex ?? 0];
        const currentCardCount = currentHand?.cards?.length ?? 0;
        const handOutcome = currentHand?.outcome;

        if (handOutcome === 'surrender' && lastRec !== 'SURRENDER') {
          markMessageIgnored(lastMsgId);
        } else if (!handOutcome && currentCardCount === prev.handCardCount && currentCardCount > 0) {
          if (lastRec === 'HIT' || lastRec === 'DOUBLE') {
            markMessageIgnored(lastMsgId);
          }
        }
      }

      prev.action = null;
      prev.insurance = null;
      prev.handCardCount = 0;
      prev.handIndex = null;
      prev.numHands = 0;
    }
  }, [recommendation, showRecommendations, addCoachMessage, currentHandIndex, updateMessageOutcome, markMessageIgnored, players, mySeatPosition, phase]);

  // ===========================================
  // OUTCOME TRACKING
  // ===========================================

  const resolvedHandsRef = useRef<Set<number>>(new Set());
  const prevInsuranceRef = useRef<{
    insuranceResult: number | null;
    playerTookInsurance: boolean;
  }>({ insuranceResult: null, playerTookInsurance: false });

  useEffect(() => {
    if (!showRecommendations || mySeatPosition === null) return;

    const player = players.get(mySeatPosition);
    if (!player) return;

    const msgIds = messageIdsRef.current;
    const hands = player.hands || [];

    // Process each hand's outcome
    hands.forEach((hand, handIndex) => {
      if (resolvedHandsRef.current.has(handIndex)) return;

      const handMsgInfo = msgIds.handMessages.get(handIndex);
      if (!handMsgInfo) return;

      if (hand.isBusted) {
        if (handMsgInfo.recommendedAction === 'STAND') {
          markMessageIgnored(handMsgInfo.messageId);
        }
        const outcome = handMsgInfo.wasSafe ? 'neutral' : 'bad';
        updateMessageOutcome(handMsgInfo.messageId, outcome);
        recordOutcome('BUST');
        resolvedHandsRef.current.add(handIndex);
        msgIds.handMessages.delete(handIndex);
        return;
      }

      if (hand.outcome) {
        let outcome: 'good' | 'bad' | 'neutral';
        let auditOutcome: string;

        if (handMsgInfo.wasSafe) {
          outcome = 'neutral';
          auditOutcome = hand.outcome === 'win' ? 'WIN' : hand.outcome === 'blackjack' ? 'BLACKJACK' : 'LOSS';
        } else if (hand.outcome === 'push') {
          outcome = 'neutral';
          auditOutcome = 'PUSH';
        } else if (hand.outcome === 'surrender') {
          outcome = 'neutral';
          auditOutcome = 'SURRENDER';
        } else {
          const isGoodOutcome = hand.outcome === 'win' || hand.outcome === 'blackjack';
          outcome = isGoodOutcome ? 'good' : 'bad';
          auditOutcome = isGoodOutcome ? (hand.outcome === 'blackjack' ? 'BLACKJACK' : 'WIN') : 'LOSS';
        }

        updateMessageOutcome(handMsgInfo.messageId, outcome);
        recordOutcome(auditOutcome);
        resolvedHandsRef.current.add(handIndex);
        msgIds.handMessages.delete(handIndex);
      }
    });

    // Clear tracking when round resets
    if (hands.length === 0 || hands.every(h => !h.cards || h.cards.length === 0)) {
      resolvedHandsRef.current.clear();
      msgIds.handMessages.clear();
      msgIds.lastActionMessageId = null;
      msgIds.lastActionWasSafe = false;
      msgIds.lastRecommendedAction = null;
    }

    // Insurance outcome tracking
    if (msgIds.lastInsuranceMessageId && msgIds.lastInsuranceRecommendation) {
      if (player.insuranceBet !== undefined && player.insuranceBet > 0) {
        prevInsuranceRef.current.playerTookInsurance = true;
      }

      if (player.insuranceDecided) {
        let shouldMarkInsurance = false;
        let dealerHadBlackjack = false;

        if (player.insuranceResult !== undefined && player.insuranceResult !== prevInsuranceRef.current.insuranceResult) {
          shouldMarkInsurance = true;
          dealerHadBlackjack = player.insuranceResult > 0;
          prevInsuranceRef.current.insuranceResult = player.insuranceResult;
        } else if (player.insuranceBet === undefined || player.insuranceBet === 0) {
          if (phase === GamePhase.PLAYING) {
            shouldMarkInsurance = true;
            dealerHadBlackjack = false;
          } else {
            for (const hand of hands) {
              if (hand.outcome) {
                shouldMarkInsurance = true;
                const dealerCards = dealer.cards || [];
                if (dealerCards.length === 2) {
                  let dealerTotal = 0;
                  for (const card of dealerCards) {
                    if (card.rank === 'A') dealerTotal += 11;
                    else if (['K', 'Q', 'J', '10'].includes(card.rank)) dealerTotal += 10;
                    else dealerTotal += parseInt(card.rank, 10);
                  }
                  dealerHadBlackjack = dealerTotal === 21;
                }
                break;
              }
            }
          }
        }

        if (shouldMarkInsurance) {
          const playerTookInsurance =
            prevInsuranceRef.current.playerTookInsurance ||
            (player.insuranceBet !== undefined && player.insuranceBet > 0) ||
            (player.insuranceResult !== undefined && player.insuranceResult !== 0);
          const playerIgnoredAdvice =
            (msgIds.lastInsuranceRecommendation === 'ACCEPT' && !playerTookInsurance) ||
            (msgIds.lastInsuranceRecommendation === 'DECLINE' && playerTookInsurance);

          if (playerIgnoredAdvice) {
            markMessageIgnored(msgIds.lastInsuranceMessageId);
          }

          const weWereRight =
            (msgIds.lastInsuranceRecommendation === 'ACCEPT' && dealerHadBlackjack) ||
            (msgIds.lastInsuranceRecommendation === 'DECLINE' && !dealerHadBlackjack);

          updateMessageOutcome(msgIds.lastInsuranceMessageId, weWereRight ? 'good' : 'bad');
          recordOutcome(weWereRight ? 'INSURANCE_WIN' : 'INSURANCE_LOSS', true);
          msgIds.lastInsuranceMessageId = null;
          msgIds.lastInsuranceRecommendation = null;
        }
      }
    }

    // Reset insurance tracking at round start
    if (hands.length === 0 || hands.every(h => !h.cards || h.cards.length === 0)) {
      prevInsuranceRef.current.insuranceResult = null;
      prevInsuranceRef.current.playerTookInsurance = false;
    }
  }, [players, mySeatPosition, showRecommendations, updateMessageOutcome, markMessageIgnored, phase, dealer, recordOutcome]);

  return recommendation;
}

// ===========================================
// HELPER HOOKS
// ===========================================

/** Get just the recommended action (simpler interface for buttons) */
export function useRecommendedAction(): StrategyAction | null {
  const { recommendedAction } = useRecommendation();
  return recommendedAction;
}

/** Get just the insurance recommendation */
export function useInsuranceRecommendation(): 'ACCEPT' | 'DECLINE' | null {
  const { insuranceRecommendation } = useRecommendation();
  return insuranceRecommendation;
}

/** Check if a specific action is recommended */
export function useIsRecommended(action: StrategyAction): boolean {
  const { recommendedAction, enabled } = useRecommendation();
  if (!enabled) return false;
  return recommendedAction === action;
}
